import { useQuery } from "@tanstack/react-query";

/** Maximum polling delay on a visible, online page (plus request latency). */
export const BETA_ACCESS_REFRESH_INTERVAL_MS = 60_000;

// Scope freshness to access only: never invalidate page data or remount forms.
// React Query shares the cache/in-flight request across all consumers. Hidden
// tabs do not poll; focus/reconnect checks catch up even if the cache is fresh.
const betaAccessQueryOptions = {
  queryKey: ["/api/app/beta/access"],
  staleTime: BETA_ACCESS_REFRESH_INTERVAL_MS,
  refetchInterval: BETA_ACCESS_REFRESH_INTERVAL_MS,
  refetchIntervalInBackground: false,
  refetchOnWindowFocus: "always" as const,
  refetchOnReconnect: "always" as const,
  retry: false,
};

/**
 * Returns the set of beta feature keys the current user has access to.
 * Mounted consumers refresh every 60 s while visible and on focus/reconnect.
 * Access lookup errors fail closed, even when a prior grant remains cached.
 */
export function useBetaAccessKeys(): string[] {
  const query = useQuery<string[]>(betaAccessQueryOptions);
  // Guard the non-array shape some unit tests stub (same pattern as the
  // team-members consumer in the dashboard).
  return !query.isError && Array.isArray(query.data) ? query.data : [];
}

/**
 * Returns true when the current user has been granted access to the given
 * beta feature key (and the feature is active). Use this in navigation items,
 * page guards, and any UI that should be completely invisible to non-members.
 *
 * @example
 *   const hasDevices = useBetaAccess("devices");
 *   if (!hasDevices) return null;
 */
export function useBetaAccess(featureKey: string): boolean {
  const keys = useBetaAccessKeys();
  return keys.includes(featureKey);
}

/**
 * Returns both the granted flag and a `loaded` boolean that becomes true once
 * the beta-access query has settled (success or error). Use `loaded` as a guard
 * before redirecting non-beta users so that users with the grant are not
 * flash-redirected during the initial loading period.
 */
export function useBetaAccessState(featureKey: string): { granted: boolean; loaded: boolean } {
  const query = useQuery<string[]>(betaAccessQueryOptions);
  return {
    granted: !query.isError && Array.isArray(query.data) && query.data.includes(featureKey),
    loaded: !query.isPending,
  };
}
